
import { useRef, useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { ChevronDown, ChevronUp, Dumbbell, Info, Plus } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { ExerciseImage } from "@/components/exercises/ExerciseImage";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useTranslations } from "@/hooks/useTranslations";

interface ExerciseSetFormProps {
  totalSets: number;
  onSetsChange: (sets: any[]) => void;
  exerciseName: string;
  exerciseDetails: {
    sets: number;
    reps: string;
    weight?: string;
    time?: string;
  };
  imageUrl?: string;
  instructions?: string[];
  isCustomExercise?: boolean;
  isCardio?: boolean;
}

export const ExerciseSetForm = ({
  totalSets,
  onSetsChange,
  exerciseName,
  exerciseDetails,
  imageUrl,
  instructions,
  isCustomExercise = false,
  isCardio = false,
}: ExerciseSetFormProps) => {
  const { t } = useTranslations("training-sessions");
  const [sets, setSets] = useState(Array.from({ length: totalSets }, (_, i) => ({
    set_number: i + 1,
    reps: "",
    weight: "",
    time: ""
  })));
  const [isOpen, setIsOpen] = useState(true);
  const tableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Update sets when totalSets changes (e.g. when adding new sets)
    if (sets.length < totalSets) {
      const newSets = [...sets];
      for (let i = sets.length; i < totalSets; i++) {
        newSets.push({
          set_number: i + 1,
          reps: "",
          weight: "",
          time: ""
        });
      }
      setSets(newSets);
      onSetsChange(newSets);
    }
  }, [totalSets, onSetsChange]);

  const handleSetChange = (index: number, field: string, value: string) => {
    const newSets = [...sets];
    newSets[index] = { ...newSets[index], [field]: value };
    setSets(newSets);
    onSetsChange(newSets);
  };
  
  const handleAddSet = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault(); // Prevent form submission
    const newSetNumber = sets.length + 1;
    const newSets = [...sets, {
      set_number: newSetNumber,
      reps: "",
      weight: "",
      time: ""
    }];
    setSets(newSets);
    onSetsChange(newSets);
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="w-full">
      <div className="flex justify-between items-center py-4 px-2 md:px-4">
        <div className="flex items-center gap-2">
          <div className="relative w-36 h-36 rounded-md overflow-hidden border border-slate-200 dark:border-gray-700">
            <ExerciseImage 
              imagePath={imageUrl} 
              altText={exerciseName} 
              fallbackText={exerciseName}
              isCustomExercise={isCustomExercise} 
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <h3 className="font-medium dark:text-white text-sm md:text-base">{exerciseName}</h3>
            <div className="flex items-center gap-1 text-xs text-slate-600 dark:text-slate-300">
              <span>{exerciseDetails.sets} {t("exercise.series")}</span>
              <span className="mx-1">•</span>
              <span>{exerciseDetails.reps} {t("exercise.reps")}</span>
              {!isCardio && exerciseDetails.weight && (
                <>
                  <span className="mx-1">•</span>
                  <span>{exerciseDetails.weight} kg</span>
                </>
              )}
              {isCardio && exerciseDetails.time && (
                <>
                  <span className="mx-1">•</span>
                  <span>{exerciseDetails.time}</span>
                </>
              )}
            </div>
          </div>
        </div>
        
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="p-1 h-8 w-8">
            {isOpen ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
        </CollapsibleTrigger>
      </div>

      <CollapsibleContent className="px-0 md:px-4">
        <div className="space-y-4">
          <div ref={tableRef} className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 dark:bg-gray-800/50">
                  <TableHead className="w-[80px] text-center dark:text-white">{t("exercise.set")}</TableHead>
                  <TableHead className="w-[120px] text-center dark:text-white">{t("exercise.reps")}</TableHead>
                  <TableHead className="w-[120px] text-center dark:text-white">
                    {isCardio ? t("exercise.time") : t("exercise.weight")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sets.map((set, index) => (
                  <TableRow key={set.set_number} className="dark:border-gray-700">
                    <TableCell className="text-center dark:text-white">{set.set_number}</TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        value={set.reps}
                        onChange={(e) => handleSetChange(index, "reps", e.target.value)}
                        placeholder={exerciseDetails.reps}
                        className="h-9 text-center bg-white dark:bg-gray-800 dark:text-white"
                      />
                    </TableCell>
                    <TableCell>
                      {isCardio ? (
                        <Input
                          type="text"
                          value={set.time}
                          onChange={(e) => handleSetChange(index, "time", e.target.value)}
                          placeholder={exerciseDetails.time || "1min"}
                          className="h-9 text-center bg-white dark:bg-gray-800 dark:text-white"
                        />
                      ) : (
                        <Input
                          type="number"
                          value={set.weight}
                          onChange={(e) => handleSetChange(index, "weight", e.target.value)}
                          placeholder={exerciseDetails.weight || "0"}
                          className="h-9 text-center bg-white dark:bg-gray-800 dark:text-white"
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="flex justify-center">
            <Button 
              onClick={handleAddSet} 
              variant="outline" 
              size="sm"
              className="flex items-center gap-1.5 dark:border-gray-700 dark:text-white dark:hover:bg-gray-800"
              type="button"
            >
              <Plus className="h-4 w-4" />
              {t("exercise.addSet")}
            </Button>
          </div>

          {instructions && instructions.length > 0 && (
            <Card className="bg-slate-50 dark:bg-gray-800/50 border border-slate-100 dark:border-gray-700">
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center gap-2 mb-2">
                  <Info className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                  <h4 className="font-medium text-sm dark:text-white">{t("exercise.instructions")}</h4>
                </div>
                <ul className="list-disc list-inside text-sm space-y-1 text-slate-700 dark:text-slate-300">
                  {instructions.map((instruction, i) => (
                    <li key={i}>{instruction}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
